import { useApolloClient, useMutation } from '@apollo/client';
import Button from '@components/Button';
import InputFieldControlled from '@components/InputFieldControlled';
import InputLogo from '@components/InputLogo';
import InputRole from '@components/InputRole';
import PasswordInput from '@components/Inputs/PasswordInput';
import PasswordChecker from '@components/PasswordChecker';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import {
    ADD_ORGANIZATION_USER_PRO,
    UPDATE_ORGANIZATION_USER_PRO,
} from '@gql/organization-users';
import { GET_PRO } from '@gql/pros';
import { generatePassword } from '@helper/string.utils';
import { useMaxCredits } from '@modules/features/MaxCreditsDisabler';
import { Button as MuiButton, CircularProgress, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { FeatureNames } from '@utils/permissions';
import { Roles } from '@utils/roleUtils';
import { useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import ButtonSaveSplit, { ButtonSaveType } from '../components/ButtonSaveSplit';
import InputHigherUp from './InputHigherUp';

const OrganizationUserForm = ({
    selectedData,
    setIsActiveForm,
    setSelectedData,
    proId,
}: {
    selectedData: any;
    setIsActiveForm: any;
    setSelectedData?: any;
    proId: string;
}) => {
    // Use a ref so the onCompleted callbacks always read the latest value without stale closures
    const typeSubmitRef = useRef<ButtonSaveType>(ButtonSaveType.SAVE_AND_RETURN);
    const client = useApolloClient();

    const invalidateOrganizationUsersCache = () => {
        client.cache.evict({ fieldName: 'getOrganizationUsersUpercut' });
        client.cache.evict({ fieldName: 'getOrganizationUserWithPaginations' });
        client.cache.gc();
    };

    const [addOrganizationUser, querySaveState] = useMutation(ADD_ORGANIZATION_USER_PRO, {
        onCompleted: () => {
            enqueueSnackbar('Nouveau membre ajouté avec succès', { variant: 'success' });
            if (typeSubmitRef.current === ButtonSaveType.SAVE_AND_RETURN) {
                setSelectedData?.(null);
                setIsActiveForm('');
            } else {
                // Stay on the form but reset all fields so the user can create another member
                methods.reset();
            }
        },
        onError: err => {
            enqueueSnackbar(err.message, {
                variant: 'error',
            });
        },
    });

    const [updateOrganizationUser, queryUpdateState] = useMutation(UPDATE_ORGANIZATION_USER_PRO, {
        onCompleted: () => {
            enqueueSnackbar('Les données des membres ont été mises à jour avec succès', { variant: 'success' });
            setSelectedData?.(null);
            setIsActiveForm('');
        },
        onError: err => {
            enqueueSnackbar(err.message, {
                variant: 'error',
            });
        },
    });

    const methods = useForm({
        mode: 'onChange',
        defaultValues: {
            password: '',
            passwordConfirm: '',
            email: selectedData?.email ?? '',
            firstName: selectedData?.firstName ?? '',
            lastName: selectedData?.lastName ?? '',
            role: selectedData?.role ?? '',
            higherUpId: selectedData?.higherUpId ?? '',
        },
    });

    const { control, setValue, watch, handleSubmit } = methods;

    const [useRandomPassword, setUseRandomPassword] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const setGeneratePassword = () => {
        const randomPassword = generatePassword();
        setValue('password', randomPassword);
        setValue('passwordConfirm', randomPassword);
    };

    const toggleRandomPassword = () => {
        !useRandomPassword && setGeneratePassword();
        setUseRandomPassword(val => !val);
    };

    const formSubmitted = async (values, _id = undefined) => {
        delete values.passwordConfirm;
        if (!_id) {
            await addOrganizationUser({
                variables: {
                    request: values,
                    proId,
                },
                updateQueries: {
                    getOrganizationUserWithPaginations: (prev, { mutationResult }) => {
                        const newUser = mutationResult.data.addOrganizationUser;
                        return {
                            getOrganizationUserWithPaginations: {
                                ...prev.getOrganizationUserWithPaginations,
                                docs: [newUser, ...prev.getOrganizationUserWithPaginations.docs],
                            },
                        };
                    },
                },
                refetchQueries: [
                    { query: GET_PRO, variables: { proId } },
                ],
                awaitRefetchQueries: true,
            });
            invalidateOrganizationUsersCache();
        } else {
            values.id = _id;
            await updateOrganizationUser({
                variables: {
                    request: values,
                    proId,
                },
                updateQueries: {
                    getOrganizationUserWithPaginations: (prev, { mutationResult }) => {
                        const newUser = mutationResult.data.updateOrganizationUser;
                        return {
                            getOrganizationUserWithPaginations: {
                                ...prev.getOrganizationUserWithPaginations,
                                docs: prev.getOrganizationUserWithPaginations.docs.map(item =>
                                    item.id === newUser.id ? newUser : item,
                                ),
                            },
                        };
                    },
                },
                refetchQueries: [
                    { query: GET_PRO, variables: { proId } },
                ],
                awaitRefetchQueries: true,
            });
            invalidateOrganizationUsersCache();
        }
    };

    const submitHandler = async values => {
        values.tosAccepted = false;
        await formSubmitted(values, selectedData?._id);
    };

    const isEditMode = Boolean(selectedData?._id);

    const adminCredits = useMaxCredits({
        feature: FeatureNames.ADMIN_USERS_CREDITS,
        proId: proId,
    });
    const memberCredits = useMaxCredits({
        feature: FeatureNames.RECRUITER_USERS_CREDITS,
        proId: proId,
    });
    const accountantCredits = useMaxCredits({
        feature: FeatureNames.ACCOUNTANT_USERS_CREDITS,
        proId: proId,
    });
    return (
        <FormProvider {...methods}>
            <DialogTitle>
                {selectedData?._id ? 'Modifier un membre' : 'Créer un nouveau membre'}
            </DialogTitle>
            <DialogContent dividers>
                <Stack>
                    <input style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} name="email" type="text" />
                    <input
                        style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }}
                        name="password"
                        type="password"
                    />
                    <InputLogo
                        name="file"
                        label="Photo de profil"
                        control={control}
                        helpText="Formats acceptés: JPG, PNG (Max: 2MB, 800x800px)"
                        previewShape="circular"
                        validation={{
                            maxSizeInMB: 2,
                            aspectRatio: 1,
                        }}
                        existingFile={selectedData?.file?.url}
                    />
                    <InputRole
                        control={control}
                        name="role"
                        label="Rôle"
                        disabled={selectedData?._id}
                        credits={{
                            [Roles.ADMIN_PRO]: adminCredits?.credits,
                            [Roles.MEMBER_PRO]: memberCredits?.credits,
                            [Roles.ACCOUNTANT_PRO]: accountantCredits?.credits,
                        }}
                    />
                    <InputHigherUp
                        name="higherUpId"
                        proId={proId}
                        control={control}
                        label="Responsable hiérarchique"
                    />
                    <InputFieldControlled
                        control={control}
                        type="email"
                        name="email"
                        label="E-mail"
                        placeholder="Veuillez saisir son email"
                    />
                    <Stack flexDirection={{ xs: "column", sm: "row" }} pt={1} gap={{  xs: 0, sm: 2 }}>
                        <InputFieldControlled
                            control={control}
                            fullWidth
                            name="firstName"
                            label="Prénom"
                            placeholder="Veuillez entrer le prénom"
                        />
                        <InputFieldControlled
                            control={control}
                            fullWidth
                            name="lastName"
                            label="Nom de famille"
                            placeholder="Veuillez entrer le nom"
                        />
                    </Stack>
                    {!selectedData?._id && (
                        <>
                            <Stack sx={{ width: 'fit-content', marginBottom: '18px' }}>
                                <FormControlLabel
                                    control={<Checkbox checked={useRandomPassword} onChange={toggleRandomPassword} />}
                                    label="Générer un mot de passe aléatoire (envoyé par email)"
                                />
                            </Stack>
                            <Stack sx={{ marginBottom: '32px' }}>
                                <Controller
                                    name="password"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <PasswordInput
                                            {...field}
                                            disabled={useRandomPassword}
                                            autoComplete="off"
                                            label="Mot de passe"
                                            placeholder="********"
                                            error={!!error}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    name="passwordConfirm"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <PasswordInput
                                            {...field}
                                            disabled={useRandomPassword}
                                            autoComplete="off"
                                            label="Confirmer le mot de passe"
                                            placeholder="********"
                                            error={!!error}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Stack>
                            {!useRandomPassword && <PasswordChecker password={watch('password')} />}
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="text"
                    color="secondary"
                    onClick={() => {
                        setIsActiveForm('');
                        setSelectedData?.(null);
                    }}>
                    Annuler
                </Button>
                {isEditMode ? (
                    <MuiButton
                        variant="contained"
                        onClick={handleSubmit(submitHandler)}
                        disabled={queryUpdateState?.loading}>
                        {queryUpdateState?.loading ? (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                        ) : (
                            <i className="fi fi-br-check" style={{ marginRight: 8 }} />
                        )}
                        Enregistrer
                    </MuiButton>
                ) : (
                    <ButtonSaveSplit
                        onClick={handleSubmit(submitHandler)}
                        onChange={val => {
                            typeSubmitRef.current = val;
                        }}
                        loading={querySaveState?.loading}
                        labels={{
                            [ButtonSaveType.SAVE_AND_RETURN]: 'Enregistrer',
                            [ButtonSaveType.SAVE_AND_ADD]: 'Ajouter un autre',
                        }}
                    />
                )}
            </DialogActions>
        </FormProvider>
    );
};


export default OrganizationUserForm;
